import { useCallback, useEffect, useState } from "react";
import { ArrowUpRight, FileText, Inbox } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { apiErrorMessage } from "@/lib/api-error";
import { CopyButton } from "@/components/ui/copy-button";
import { useDolfinClient } from "@/components/DolfinProvider";
import { formatDateTime } from "@/lib/format";
import {
  EmptyState,
  ErrorState,
  HeaderRow,
  LoadingState,
  Pager,
  TH,
  TableShell,
} from "@/components/PageUi";
import type { components } from "@dolfin/api-admin";

type InboundEmail = components["schemas"]["InboundEmailResponse"];
type Attachment = components["schemas"]["InboundEmailAttachmentResponse"];
type Cursors = { current?: string | null; next?: string | null; previous?: string | null };

const PAGE_SIZE = 20;

const MAX_ENVELOPE_CHARS = 20;

/** Kept to one line so the attachments column stays visible; the full value is a hover or a copy away. */
function EnvelopeValue({ value, className }: { value: string; className?: string }) {
  const truncated = value.length > MAX_ENVELOPE_CHARS;
  const shown = truncated ? `${value.slice(0, MAX_ENVELOPE_CHARS)}\u2026` : value;

  return (
    <span className="inline-flex items-start gap-1">
      {truncated ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={className}>{shown}</span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs break-all">{value}</TooltipContent>
        </Tooltip>
      ) : (
        <span className={className}>{shown}</span>
      )}
      <CopyButton value={value} className="shrink-0 [&_svg]:size-3" />
    </span>
  );
}

/** Why we did not take a file, in words a person forwarding a bill would use. */
function skipReasonCopy(reason: Attachment["skipReason"]): string {
  switch (reason) {
    case "InlineImage":
      return "part of the email, not an attachment";
    case "UnsupportedFileType":
      return "file type not supported";
    case "FileTooLarge":
      return "too large";
    case "InvalidContent":
      return "could not be read";
    case "UploadFailed":
      return "failed on our side, please re-send";
    default:
      return "not taken";
  }
}

function rejectionCopy(reason: InboundEmail["rejectionReason"]): string {
  switch (reason) {
    case "SenderNotAllowed":
      return "sender not allowed";
    case "AuthenticationFailed":
      return "failed email authentication";
    case "TokenNotResolved":
      return "unknown forwarding address";
    case "TenantNotFound":
      return "forwarding address no longer exists";
    default:
      return "refused";
  }
}

const TONE = {
  ok: "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  warn: "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400",
  bad: "border-destructive/40 bg-destructive/10 text-destructive",
};

const capitalise = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

function StatusBadge({ email }: { email: InboundEmail }) {
  const ingested = Number(email.ingestedCount);
  const total = Number(email.attachmentCount);

  if (email.status === "Ingested")
    return (
      <Badge variant="outline" className={TONE.ok}>
        {ingested === 1 ? "1 bill" : `${ingested} bills`}
      </Badge>
    );
  if (email.status === "PartiallyIngested")
    return (
      <Badge variant="outline" className={TONE.warn}>
        {ingested} of {total} taken
      </Badge>
    );
  if (email.status === "Rejected")
    return (
      <Badge variant="outline" className={TONE.bad}>
        {capitalise(rejectionCopy(email.rejectionReason))}
      </Badge>
    );
  if (email.status === "NoBillAttachments")
    return (
      <Badge variant="outline" className={TONE.bad}>
        Nothing taken
      </Badge>
    );
  if (email.status === "Failed")
    return (
      <Badge variant="outline" className={TONE.bad}>
        Failed
      </Badge>
    );
  return <Badge variant="outline">Processing</Badge>;
}

function AttachmentsCell({
  email,
  onBillClick,
}: {
  email: InboundEmail;
  onBillClick?: (billId: string) => void;
}) {
  if (email.attachments.length === 0)
    return <span className="text-xs text-muted-foreground">No attachments</span>;

  return (
    <ul className="space-y-1">
      {email.attachments.map((a) => (
        <li key={a.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
          <span className={cn("inline-flex items-center gap-1.5", a.ingested ? "text-foreground" : "text-muted-foreground")}>
            <FileText className="size-3.5 shrink-0 text-muted-foreground" />
            {a.fileName}
          </span>
          {a.ingested ? (
            <>
              {a.billId && onBillClick ? (
                <button
                  type="button"
                  onClick={() => onBillClick(a.billId!)}
                  className="inline-flex items-center gap-0.5 font-medium text-emerald-700 underline-offset-4 hover:underline dark:text-emerald-400"
                >
                  Bill created <ArrowUpRight className="size-3" />
                </button>
              ) : (
                <span className="text-emerald-700 dark:text-emerald-400">Bill created</span>
              )}
            </>
          ) : (
            <span className="text-muted-foreground">
              {skipReasonCopy(a.skipReason)}
              {a.skipReason === "UnsupportedFileType" && a.declaredContentType
                ? ` (${a.declaredContentType})`
                : ""}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

/** Every email that reached the forwarding address, and for files not taken, why. */
export function InboundEmailsList({ onBillClick }: { onBillClick?: (billId: string) => void } = {}) {
  const api = useDolfinClient();

  const [emails, setEmails] = useState<InboundEmail[] | null>(null);
  const [cursors, setCursors] = useState<Cursors | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ingested, setIngested] = useState<boolean | undefined>(undefined);
  const [cursor, setCursor] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    const query: Record<string, string | number | boolean> = { Limit: PAGE_SIZE };
    if (ingested !== undefined) query.Ingested = ingested;
    if (cursor) query.Cursor = cursor;

    const { data, error: apiError } = await api.GET("/v1/bills/inbound-emails", {
      params: { query },
    });

    if (apiError || !data) {
      setError(apiErrorMessage(apiError, "Couldn't load your inbound emails."));
      return;
    }
    setEmails(data.data ?? []);
    setCursors(data.meta?.cursors ?? null);
    setError(null);
  }, [api, ingested, cursor]);

  useEffect(() => {
    void load();
  }, [load]);

  function filter(next: boolean | undefined) {
    setIngested(next);
    setCursor(null);
    setPage(1);
    setEmails(null);
  }

  /** Cursor pagination has no page count, so the number is kept by counting the steps taken. */
  function goTo(nextCursor: string | null | undefined, step: 1 | -1) {
    setEmails(null);
    setCursor(nextCursor ?? null);
    setPage((p) => Math.max(1, p + step));
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <p className="max-w-xl text-sm text-pretty text-muted-foreground">
          Everything sent to your forwarding address, newest first, whether or not it became a bill. If an
          invoice never appeared, it is listed here with the reason. Only filenames and types are kept, never
          the attachment itself.
        </p>
        <Select
          value={ingested === undefined ? "all" : ingested ? "taken" : "refused"}
          onValueChange={(v) => filter(v === "all" ? undefined : v === "taken")}
        >
          <SelectTrigger className="h-9 w-44" aria-label="Filter inbound emails">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All emails</SelectItem>
            <SelectItem value="taken">Became bills</SelectItem>
            <SelectItem value="refused">Nothing taken</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {error ? (
        <ErrorState message={error} onRetry={() => void load()} />
      ) : emails === null ? (
        <LoadingState label="Loading inbound emails..." />
      ) : emails.length === 0 ? (
        <EmptyState icon={Inbox} title={ingested === false ? "Nothing turned away" : "No emails yet"}>
          {ingested === false
            ? "Every email sent here produced a bill."
            : ingested === true
              ? "No forwarded email has become a bill yet."
              : "Forward a bill to the address above and it will appear here."}
        </EmptyState>
      ) : (
        <TableShell>
          <Table>
            <TableHeader>
              <HeaderRow>
                <TableHead className={TH}>Email</TableHead>
                <TableHead className={TH}>Attachments</TableHead>
                <TableHead className={TH}>Received</TableHead>
                <TableHead className={cn(TH, "text-right")}>Outcome</TableHead>
              </HeaderRow>
            </TableHeader>
            <TableBody>
              {emails.map((email) => (
                <TableRow key={email.id} className="align-top">
                  <TableCell className="max-w-[18rem]">
                    <div className="flex min-w-0 items-start gap-3">
                      <div className="min-w-0">
                        <EnvelopeValue value={email.fromAddress} className="font-mono text-xs font-medium" />
                        <div className="text-xs text-muted-foreground">
                          {email.subject ? <EnvelopeValue value={email.subject} /> : "(no subject)"}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <AttachmentsCell email={email} onBillClick={onBillClick} />
                  </TableCell>
                  <TableCell className="text-xs whitespace-nowrap text-muted-foreground tabular-nums">
                    {formatDateTime(email.receivedAt)}
                  </TableCell>
                  <TableCell className="text-right">
                    <StatusBadge email={email} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableShell>
      )}

      <Pager
        page={page}
        hasPrevious={!!cursors?.previous}
        hasNext={!!cursors?.next}
        onPrevious={() => goTo(cursors?.previous, -1)}
        onNext={() => goTo(cursors?.next, 1)}
      />
    </div>
  );
}
